/**
 * telegram.js  —  BharatQA frontend audit notifications
 *
 * Configure in Vercel (or .env.local during dev):
 *   REACT_APP_TELEGRAM_BOT_TOKEN   = 7xxxxxxx:AAxxxxxxxxxxxxxxxx
 *   REACT_APP_TELEGRAM_CHAT_ID     = -100xxxxxxxxxx   (group) or numeric user ID
 *
 * Messages are fire-and-forget: failures are silently swallowed so they
 * never break the user-facing flow.
 */

const BOT_TOKEN = process.env.REACT_APP_TELEGRAM_BOT_TOKEN;
const CHAT_ID   = process.env.REACT_APP_TELEGRAM_CHAT_ID;

/**
 * Send a plain-text Telegram message.  Returns a promise but callers
 * don't need to await it — any rejection is caught internally.
 */
async function sendTelegram(message) {
  if (!BOT_TOKEN || !CHAT_ID) return; // env vars not configured — skip silently
  try {
    await fetch(`https://api.telegram.org/bot${BOT_TOKEN}/sendMessage`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        chat_id: CHAT_ID,
        text: message,
        parse_mode: 'HTML',
      }),
    });
  } catch (_) {
    // Never crash the app for a notification failure
  }
}

// ── IST helpers ────────────────────────────────────────────────────────────
function nowIST() {
  return new Date().toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
}

// ── Public API ─────────────────────────────────────────────────────────────

/**
 * Fired when a company user successfully signs in via Google.
 * @param {{ email?: string, company_name?: string }} company
 */
export function notifySignIn(company) {
  const ts   = nowIST();
  const name = company?.company_name || 'Unknown Company';
  const email = company?.email || '—';

  sendTelegram(
`🔔 <b>New Sign-In · BharatQA</b>

👤 <b>Company:</b> ${name}
📧 <b>Email:</b> ${email}
🕐 <b>Time (IST):</b> ${ts}`
  );
}

/**
 * Fired when a company submits a new test session for approval.
 * @param {{ app_name?: string, company_name?: string, email?: string }} company
 * @param {{ app_name?: string, test_instructions?: string }} form
 * @param {string} planName   e.g. "Pro"
 * @param {number} planPrice  e.g. 4999
 */
export function notifyTestSubmission(company, form, planName, planPrice) {
  const ts       = nowIST();
  const compName = company?.company_name || 'Unknown';
  const email    = company?.email || '—';
  const app      = form?.app_name || '—';
  const plan     = planName  || '—';
  const price    = planPrice ? `₹${Number(planPrice).toLocaleString('en-IN')}` : '—';

  // First ~100 chars of instructions to give context
  let instructions = (form?.test_instructions || '').trim();
  if (instructions.length > 120) instructions = instructions.slice(0, 117) + '...';

  sendTelegram(
`🚀 <b>New Test Submitted · BharatQA</b>

📱 <b>App:</b> ${app}
🏢 <b>Company:</b> ${compName} (${email})
📦 <b>Plan:</b> ${plan} — ${price}
📝 <b>Instructions:</b> ${instructions || 'N/A'}
🕐 <b>Time (IST):</b> ${ts}`
  );
}
